import React from 'react';
import { PageAccountStoryblok } from '../../../_shared/interfaces/storyblok';
import { StoryblokCommon, getRichTextMarkup } from '../../../lib/storyblok/storyblok';

import * as AccountCompletionStyles from './account-completion.module.scss';
import * as FormStylesHs from '../../../_scss/modules/_forms.hs.module.scss';
import * as FormStylesDr from '../../../_scss/modules/_forms.dr.module.scss';

import * as RichtextStyles from '../../../_scss/modules/_richtext.module.scss';
import HsButton from '../hs-button/hs-button';
import AccountContext from '../../../context/account/account.context';
import { IAddress } from '../../../_shared/interfaces/address';
import { IAccountData } from '../../../_shared/interfaces/account-data';
import LoadingAnimationContext from '../../../context/loading-animation/loading-animation.context';
import { replaceText } from '../../../lib/helpers/helpers';

const AccountCompletion = ({
    blok,
    missingData,
    storyblokCommon,
    closeModal,
    accountData,
    addresses,
}: {
    blok: PageAccountStoryblok;
    missingData: string[];
    storyblokCommon: StoryblokCommon;
    closeModal: () => void;
    accountData?: IAccountData;
    addresses?: IAddress[];
}) => {
    const FormStyles = storyblokCommon.configuration.content.theme === 'dr' ? FormStylesDr : FormStylesHs;
    const accountContext = React.useContext(AccountContext);
    const { showLoadingAnimation, hideLoadingAnimation } = React.useContext(LoadingAnimationContext);
    const [validationErrors, setValidationErrors] = React.useState<string[]>([]);
    const professionValues =
        (storyblokCommon.configuration.content.professions_sectors &&
            storyblokCommon.configuration.content.professions_sectors[0]?.values) ||
        [];
    const defaultProfessionValue =
        (storyblokCommon.configuration.content.professions_sectors &&
            storyblokCommon.configuration.content.professions_sectors[0]?.default_value) ||
        '';
    const [profession, setProfession] = React.useState<string>('');
    const [contractNumber, setContractNumber] = React.useState<string>((accountData && accountData.contractNumber) || '');
    const [contractCheckboxDisabled, setContractCheckboxDisabled] = React.useState<boolean>(true);
    const [firstName, setFirstName] = React.useState<string>((accountData && accountData.firstName) || '');
    const [lastName, setLastName] = React.useState<string>((accountData && accountData.lastName) || '');
    const [phoneNumber, setPhoneNumber] = React.useState<string>((accountData && accountData.phoneNumber) || '');
    const [company, setCompany] = React.useState<string>((addresses && addresses[0] && addresses[0].company) || '');
    const [supplement, setSupplement] = React.useState<string>((addresses && addresses[0] && addresses[0].supplement) || '');
    const [street, setStreet] = React.useState<string>((addresses && addresses[0] && addresses[0].street) || '');
    const [city, setCity] = React.useState<string>((addresses && addresses[0] && addresses[0].city) || '');
    const [zip, setZip] = React.useState<string>((addresses && addresses[0] && addresses[0].postalCode) || '');
    const [country, setCountry] = React.useState<string>((addresses && addresses[0] && addresses[0].country) || '');

    const MAX_FIRST_NAME = 35;
    const MAX_LAST_NAME = 35;
    const MAX_PHONE_NUMBER = 35;
    const MAX_COMPANY = 40;
    const MAX_SUPPLEMENT = 40;
    const MAX_CITY = 40;
    const MAX_STREET = 35;
    const MAX_ZIP = 40;

    const isContractNumberValid = () => {
        return contractNumber.length === 10 && contractNumber.startsWith('230');
    };

    const replaceDisclaimerText = (text: string | undefined): string | undefined => {
        if (!text) {
            return text;
        }
        let results: string = replaceText(
            text,
            '%NAME%',
            (firstName ?? accountData?.firstName ?? '') + ' ' + (lastName ?? accountData?.lastName ?? ''),
        );
        results = replaceText(results, '%FIRMA%', company);
        return results;
    };

    React.useEffect(() => {
        if (isContractNumberValid()) {
            setContractCheckboxDisabled(false);
        } else {
            setContractCheckboxDisabled(true);
        }
    }, [contractNumber]);

    React.useEffect(() => {
        if (company && company.length > MAX_COMPANY) {
            setCompany('');
        }
    }, [company]);

    React.useEffect(() => {
        if (supplement && supplement.length > MAX_SUPPLEMENT) {
            setSupplement('');
        }
    }, [supplement]);

    const validateData = () => {
        const errors: string[] = [];
        if (missingData.includes('firstName')) {
            if (firstName.length < 2) {
                errors.push('firstName');
            }
        }

        if (missingData.includes('lastName')) {
            if (lastName.length < 2) {
                errors.push('lastName');
            }
        }

        if (missingData.includes('phoneNumber')) {
            if (phoneNumber.length < 2) {
                errors.push('phoneNumber');
            }
        }

        if (missingData.includes('address')) {
            if (company.length < 2) {
                errors.push('addressCompany');
            }

            if (street.length < 2) {
                errors.push('addressStreet');
            }

            if (zip.length < 2) {
                errors.push('addressZip');
            }

            if (city.length < 2) {
                errors.push('addressCity');
            }

            if (country.length < 2) {
                errors.push('addressCountry');
            }
        }

        if (missingData.includes('profession')) {
            if ((document.getElementById('professionCompletion') as HTMLInputElement).value.length < 2) {
                errors.push('profession');
            }
        }

        if (missingData.includes('business')) {
            if (profession.length < 2) {
                errors.push('professionSector');
            }
        }

        if (
            (document.getElementById('customerNumberInput') as HTMLInputElement) &&
            (document.getElementById('customerNumberInput') as HTMLInputElement).value.length > 0
        ) {
            if (
                (document.getElementById('customerNumberInput') as HTMLInputElement).value.length < 5 ||
                (document.getElementById('customerNumberInput') as HTMLInputElement).value.length > 10
            ) {
                errors.push('customerNumber');
            }
        }

        if (
            (document.getElementById('contractNumberInput') as HTMLInputElement) &&
            (document.getElementById('contractNumberInput') as HTMLInputElement).value.length > 0
        ) {
            if (!isContractNumberValid()) {
                errors.push('contractNumber');
            }
        }

        setValidationErrors(errors);

        return errors.length === 0;
    };

    const submitAccountData = () => {
        if (!validateData()) return;

        showLoadingAnimation();

        const newAccountData = { ...accountData };

        missingData.forEach((data) => {
            switch (data) {
                case 'firstName':
                    newAccountData.firstName = firstName;
                    break;
                case 'lastName':
                    newAccountData.lastName = lastName;
                    break;
                case 'phoneNumber':
                    newAccountData.phoneNumber = phoneNumber;
                    break;
                case 'address':
                    // overwrite old address
                    if (addresses && addresses[0]) {
                        const address = {
                            ...addresses[0],
                            company,
                            supplement,
                            street,
                            postalCode: zip,
                            city,
                            country,
                        };
                        accountContext.updateAddress(address);
                    }
                    // create new address
                    else {
                        const address: IAddress = {
                            company,
                            supplement,
                            street,
                            postalCode: zip,
                            city,
                            country,
                        };
                        accountContext.addAddress(address);
                    }
                    break;
                case 'profession':
                    newAccountData.profession =
                        (document.getElementById('professionCompletion') as HTMLInputElement) &&
                        (document.getElementById('professionCompletion') as HTMLInputElement).value;
                    break;
                case 'business':
                    newAccountData.business = profession;
                    break;
                default:
                    break;
            }
        });

        if (
            (document.getElementById('customerNumberInput') as HTMLInputElement) &&
            (document.getElementById('customerNumberInput') as HTMLInputElement).value.length >= 5 &&
            (document.getElementById('customerNumberInput') as HTMLInputElement).value.length <= 10
        ) {
            newAccountData.customerNumber = (document.getElementById('customerNumberInput') as HTMLInputElement).value;
        }

        if ((document.getElementById('contractNumberInput') as HTMLInputElement) && isContractNumberValid()) {
            newAccountData.contractNumber = (document.getElementById('contractNumberInput') as HTMLInputElement).value;
        }

        if (!accountData || !accountData.disclaimerContractData) {
            newAccountData.disclaimerContractData = {
                date: new Date(),
                disclaimerText: replaceDisclaimerText(blok.profile_edit_contract_number[0].checkbox_label),
            };
        }

        accountContext.updateAccountData(newAccountData).finally(() => {
            accountContext.updateToken();
            hideLoadingAnimation();
            closeModal();
        });
    };

    const abortAccountCompletion = () => {
        accountContext.signOut();
        closeModal();
    };

    if (!blok.account_completion || !blok.account_completion[0]) {
        closeModal();
        return null;
    }

    return (
        <div className={AccountCompletionStyles.content}>
            <h4>{blok.account_completion[0].headline_text}</h4>
            <div className={RichtextStyles.richtext} dangerouslySetInnerHTML={getRichTextMarkup(blok.account_completion[0].richtext_top)} />
            <div className={AccountCompletionStyles.accountCompletion}>
                {/* check if firstName is in missingData */}
                {(missingData.includes('firstName') || missingData.includes('lastName')) && (
                    <div>
                        <h5>{blok.account_completion[0].name_headline}</h5>
                        {missingData.includes('firstName') && (
                            <div className={FormStyles.formField}>
                                <label htmlFor="firstNameCompletion">{blok.profile_edit_name[0].label_first_name}*</label>
                                <input
                                    id="firstNameCompletion"
                                    type="text"
                                    placeholder={blok.profile_edit_name[0].label_first_name}
                                    maxLength={MAX_FIRST_NAME}
                                    onChange={(event) => {
                                        setFirstName(event.target.value);
                                    }}
                                />
                                {validationErrors.includes('firstName') && (
                                    <p className={FormStyles.error}>{blok.profile_edit_name[0].error_first_name}</p>
                                )}
                            </div>
                        )}
                        {missingData.includes('lastName') && (
                            <div className={FormStyles.formField}>
                                <label htmlFor="lastNameCompletion">
                                    {blok.profile_edit_name && blok.profile_edit_name[0].label_last_name}*
                                </label>
                                <input
                                    id="lastNameCompletion"
                                    type="text"
                                    placeholder={blok.profile_edit_name[0].label_last_name}
                                    maxLength={MAX_LAST_NAME}
                                    onChange={(event) => {
                                        setLastName(event.target.value);
                                    }}
                                />
                                {validationErrors.includes('lastName') && (
                                    <p className={FormStyles.error}>{blok.profile_edit_name[0].error_last_name}</p>
                                )}
                            </div>
                        )}
                    </div>
                )}
                {/* check if phonenumber is in missingData */}
                {missingData.includes('phoneNumber') && (
                    <div>
                        <h5>{blok.account_completion[0].phone_number_headline}</h5>
                        {missingData.includes('phoneNumber') && (
                            <div className={FormStyles.formField}>
                                <label htmlFor="phoneNumberCompletion">
                                    {blok.profile_edit_phone && blok.profile_edit_phone[0].label}*
                                </label>
                                <input
                                    id="phoneNumberCompletion"
                                    type="text"
                                    placeholder={blok.profile_edit_phone[0].label}
                                    maxLength={MAX_PHONE_NUMBER}
                                    onChange={(event) => {
                                        setPhoneNumber(event.target.value);
                                    }}
                                />
                                {validationErrors.includes('phoneNumber') && (
                                    <p className={FormStyles.error}>{blok.profile_edit_phone[0].error}</p>
                                )}
                            </div>
                        )}
                    </div>
                )}
                {/* check if address is in missingData */}
                {missingData.includes('address') && (
                    <div>
                        <h5>{blok.account_completion[0].address_headline}</h5>
                        <div className={AccountCompletionStyles.addressBox}>
                            <div className={FormStyles.formField}>
                                <label htmlFor="addressCompanyCompletion">
                                    {storyblokCommon.configuration.content.address_company_label}*
                                </label>
                                <input
                                    type="text"
                                    id="addressCompanyCompletion"
                                    placeholder={storyblokCommon.configuration.content.address_company_label}
                                    maxLength={MAX_COMPANY}
                                    onChange={(event) => {
                                        setCompany(event.target.value);
                                    }}
                                    value={company}
                                />
                                {validationErrors.includes('addressCompany') && (
                                    <p className={FormStyles.error}>{storyblokCommon.configuration.content.address_company_error}</p>
                                )}
                            </div>
                            <div className={FormStyles.formField}>
                                <label htmlFor="addressAdditionalCompletion">
                                    {storyblokCommon.configuration.content.address_supplement_label}
                                </label>
                                <input
                                    type="text"
                                    id="addressAdditionalCompletion"
                                    placeholder={storyblokCommon.configuration.content.address_supplement_label}
                                    maxLength={MAX_SUPPLEMENT}
                                    onChange={(event) => {
                                        setSupplement(event.target.value);
                                    }}
                                    value={supplement}
                                />
                            </div>
                            <div className={FormStyles.formField}>
                                <label htmlFor="addressStreetCompletion">
                                    {storyblokCommon.configuration.content.address_street_label}*
                                </label>
                                <input
                                    type="text"
                                    id="addressStreetCompletion"
                                    placeholder={storyblokCommon.configuration.content.address_street_label}
                                    maxLength={MAX_STREET}
                                    onChange={(event) => {
                                        setStreet(event.target.value);
                                    }}
                                    value={street}
                                />
                                {validationErrors.includes('addressStreet') && (
                                    <p className={FormStyles.error}>{storyblokCommon.configuration.content.address_street_error}</p>
                                )}
                            </div>
                            <div className={AccountCompletionStyles.cityBox}>
                                <div className={FormStyles.formField}>
                                    <label htmlFor="addressZipCompletion">
                                        {storyblokCommon.configuration.content.address_postal_code_label}*
                                    </label>
                                    <input
                                        type="text"
                                        id="addressZipCompletion"
                                        placeholder={storyblokCommon.configuration.content.address_postal_code_label}
                                        maxLength={MAX_ZIP}
                                        onChange={(event) => {
                                            setZip(event.target.value);
                                        }}
                                        value={zip}
                                    />
                                    {validationErrors.includes('addressZip') && (
                                        <p className={FormStyles.error}>
                                            {storyblokCommon.configuration.content.address_postal_code_error}
                                        </p>
                                    )}
                                </div>
                                <div className={FormStyles.formField}>
                                    <label htmlFor="addressCityCompletion">
                                        {storyblokCommon.configuration.content.address_city_label}*
                                    </label>
                                    <input
                                        type="text"
                                        id="addressCityCompletion"
                                        placeholder={storyblokCommon.configuration.content.address_city_label}
                                        maxLength={MAX_CITY}
                                        onChange={(event) => {
                                            setCity(event.target.value);
                                        }}
                                        value={city}
                                    />
                                    {validationErrors.includes('addressCity') && (
                                        <p className={FormStyles.error}>{storyblokCommon.configuration.content.address_city_error}</p>
                                    )}
                                </div>
                            </div>

                            <div className={FormStyles.formField}>
                                <label htmlFor="addressCountryCompletion">
                                    {storyblokCommon.configuration.content.address_country_label}*
                                </label>
                                <select
                                    id="addressCountryCompletion"
                                    autoComplete="country"
                                    name="country"
                                    onChange={(event) => {
                                        setCountry(event.target.value);
                                    }}
                                    value={country}
                                >
                                    <option value="">{storyblokCommon.configuration.content.select_default}</option>
                                    {storyblokCommon.configuration.content.countries.map((m) => {
                                        return (
                                            <option key={m._uid} value={m.id}>
                                                {m.name}
                                            </option>
                                        );
                                    })}
                                </select>
                                {validationErrors.includes('addressCountry') && (
                                    <p className={FormStyles.error}>{storyblokCommon.configuration.content.address_country_error}</p>
                                )}
                            </div>
                        </div>
                    </div>
                )}
                {/* check if profession or business is in missingData */}
                {(missingData.includes('profession') || missingData.includes('business')) && (
                    <div>
                        <h5>{blok.account_completion[0].profession_headline}</h5>
                        {missingData.includes('business') && (
                            <div className={FormStyles.formField}>
                                <label htmlFor="professionSectorCompletion">
                                    {blok.profile_edit_profession_sector && blok.profile_edit_profession_sector[0].label}*
                                </label>
                                <select name="sector" onChange={(e) => setProfession(e.target.value)}>
                                    <option selected={true} value="">
                                        {defaultProfessionValue}
                                    </option>
                                    {professionValues.map((option, index) => {
                                        return (
                                            <option key={index} value={option}>
                                                {option}
                                            </option>
                                        );
                                    })}
                                </select>
                                {validationErrors.includes('professionSector') && (
                                    <p className={FormStyles.error}>
                                        {blok.profile_edit_profession_sector && blok.profile_edit_profession_sector[0].error_label}
                                    </p>
                                )}
                            </div>
                        )}
                        {missingData.includes('profession') && (
                            <div className={FormStyles.formField}>
                                <label htmlFor="professionCompletion">
                                    {blok.profile_edit_profession && blok.profile_edit_profession[0].label}*
                                </label>
                                <input
                                    type="text"
                                    id="professionCompletion"
                                    placeholder={blok.profile_edit_profession && blok.profile_edit_profession[0].label}
                                    maxLength={100}
                                />
                                {validationErrors.includes('profession') && (
                                    <p className={FormStyles.error}>
                                        {blok.profile_edit_profession && blok.profile_edit_profession[0].error_label}
                                    </p>
                                )}
                            </div>
                        )}
                    </div>
                )}
                {(!accountData?.customerNumber ||
                    accountData.customerNumber.length === 0 ||
                    !accountData?.contractNumber ||
                    accountData.contractNumber.length === 0) && (
                    <div>
                        <h5>{blok.account_completion[0].data_headline}</h5>
                        <div
                            className={RichtextStyles.richtext}
                            dangerouslySetInnerHTML={getRichTextMarkup(blok.account_completion[0].data_text)}
                        />
                        {(!accountData?.customerNumber || accountData.customerNumber.length === 0) && (
                            <div className={FormStyles.formField}>
                                <label htmlFor="customerNumberInput">
                                    {blok.profile_edit_client_number && blok.profile_edit_client_number[0].Label}
                                </label>
                                <input
                                    type="text"
                                    id="customerNumberInput"
                                    placeholder={blok.profile_edit_client_number && blok.profile_edit_client_number[0].Label}
                                    value={accountData && accountData.customerNumber}
                                    minLength={5}
                                    maxLength={10}
                                />
                                {validationErrors.includes('customerNumber') && (
                                    <p className={FormStyles.error}>
                                        {blok.profile_edit_client_number && blok.profile_edit_client_number[0].label_error}
                                    </p>
                                )}
                            </div>
                        )}
                        {(!accountData?.contractNumber || accountData.contractNumber.length === 0) && (
                            <div className={AccountCompletionStyles.contractNumberContainer}>
                                <div className={FormStyles.formField}>
                                    <label htmlFor="contractNumberInput">
                                        {blok.profile_edit_contract_number && blok.profile_edit_contract_number[0].label}
                                    </label>
                                    <input
                                        type="text"
                                        id="contractNumberInput"
                                        onChange={(e) => setContractNumber(e.target.value)}
                                        placeholder={blok.profile_edit_contract_number && blok.profile_edit_contract_number[0].label}
                                        value={accountData && accountData.contractNumber}
                                        minLength={10}
                                        maxLength={10}
                                    />
                                    {validationErrors.includes('contractNumber') && (
                                        <p className={FormStyles.error}>
                                            {blok.profile_edit_contract_number && blok.profile_edit_contract_number[0].label_error}
                                        </p>
                                    )}
                                </div>
                                <div className={FormStyles.formField}>
                                    <input
                                        type="checkbox"
                                        id="contractCheckbox"
                                        checked={accountData && accountData.disclaimerContractData && true}
                                        disabled={contractCheckboxDisabled}
                                    />
                                    {blok.profile_edit_contract_number && (
                                        <label htmlFor="contractCheckbox" className={FormStyles.checkboxLabel}>
                                            {blok.profile_edit_contract_number &&
                                                replaceDisclaimerText(blok.profile_edit_contract_number[0].checkbox_label)}
                                        </label>
                                    )}
                                    {validationErrors.includes('contractCheckbox') && (
                                        <p className={FormStyles.error}>
                                            {blok.profile_edit_contract_number && blok.profile_edit_contract_number[0].checkbox_error}
                                        </p>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                )}
                <div className={AccountCompletionStyles.buttonBox}>
                    <HsButton storyblokCommon={storyblokCommon} appearance="secondary" onClick={abortAccountCompletion}>
                        Abbrechen
                    </HsButton>
                    <HsButton storyblokCommon={storyblokCommon} appearance="primary" onClick={submitAccountData}>
                        Speichern
                    </HsButton>
                </div>
            </div>
        </div>
    );
};

export default AccountCompletion;
